import React from 'react';
import { NavLink } from 'react-router-dom';
import logo from '../assets/concreto_tec_logo.png';
import {
  Building2,
  LayoutDashboard,
  Users,
  Building,
  Factory,
  Hammer,
  Truck,
  Warehouse,
  ClipboardCheck,
  Briefcase,
  Phone,
  X,
  Pin,
  PinOff,
  ClipboardPen,
  ListOrdered,
  ClipboardList,
  ListChecks,
  SlidersHorizontal,
  BarChart3
} from 'lucide-react';

const menuSections = [
  {
    items: [{ path: '/app', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    section: 'Cadastros principais',
    items: [
      //{ path: '/app/clientes', label: 'Clientes', icon: Briefcase },
      { path: '/app/usuarios', label: 'Usuários', icon: Users },
      { path: '/app/empresas', label: 'Empresas', icon: Building },
      { path: '/app/contatos', label: 'Contatos', icon: Phone },
      { path: '/app/obras', label: 'Obras', icon: Building2 },
      { path: '/app/cadastros-tecnicos', label: 'Cadastros técnicos', icon: SlidersHorizontal },
    ],
  },
  {
    section: 'Concretagem',
    items: [
      { path: '/app/moldadores', label: 'Moldadores', icon: Hammer },
      { path: '/app/concretagens', label: 'Concretagens', icon: Factory },
      { path: '/app/concreteiras', label: 'Concreteiras', icon: Warehouse },
      { path: '/app/caminhoes', label: 'Caminhoes', icon: Truck },
      { path: '/app/gestao-concretagem', label: 'Gestão de concretagem', icon: ListChecks },
    ],
  },
  {
    section: 'Relatórios',
    items: [
      { path: '/app/ficha-ruptura', label: 'Ficha de ruptura', icon: ClipboardPen },
      { path: '/app/resultados-sequencial', label: 'Resultados sequencial', icon: ListOrdered },
      { path: '/app/relatorio-caminhao', label: 'Relatório por caminhão', icon: ClipboardList },
    ],
  },
  {
    section: 'Medições',
    items: [
      { path: '/app/medicao-moldagem', label: 'Medição - Moldagem', icon: ClipboardList },
      { path: '/app/medicao-ruptura', label: 'Medição - Ruptura', icon: ClipboardList },
    ],
  },
  {
    section: 'Ensaios',
    items: [
      { path: '/app/resultados-corpos-prova', label: 'Resultados', icon: ClipboardCheck },
      { path: '/app/revisao-resultados', label: 'Revisão de resultados', icon: BarChart3 },
    ],
  },
];

const linkBase =
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition';

// Monta as classes da sidebar conforme estado colapsado e variante (drawer mobile via className).
function buildSidebarClassName(className, isCollapsed) {
  const base = 'flex flex-col border-r border-slate-200 bg-white';
  if (!className) {
    return `${base} hidden md:flex md:transition-[width] md:duration-200 md:ease-out ${isCollapsed ? 'md:w-20 sidebar-collapsed' : 'md:w-64 sidebar-expanded'
      }`;
  }
  return `${base} sidebar-expanded ${className}`.trim();
}

// Filtra o menu por papel: oculta a tela de Clientes para quem não é admin.
function filterMenuByRole(role) {
  if (role === 'admin') {
    return menuSections;
  }

  return menuSections
    .map((section) => {
      if (!section.items) {
        return section;
      }
      const filteredItems = section.items.filter((item) => item.path !== '/app/clientes');
      return { ...section, items: filteredItems };
    })
    .filter((section) => !section.items || section.items.length > 0);
}

// Menu de navegação lateral: agrupado por seção, colapsável, com itens filtrados pelo papel do usuário.
function Sidebar({ className, onClose, isCollapsed = false, onToggleCollapse, role }) {
  // Os textos passam a ser controlados por animacao CSS (sidebar-reveal) para suavizar a abertura no hover.
  const brandTextClassName = 'sidebar-reveal sidebar-brand-text';
  const linkLabelClassName = 'sidebar-reveal sidebar-link-label';
  const sectionClassName = 'sidebar-reveal sidebar-group-title';
  const footerClassName = 'sidebar-reveal';
  const linkClassName = `${linkBase} sidebar-link ${isCollapsed ? 'md:justify-center' : 'md:justify-start'}`;
  const visibleSections = filterMenuByRole(role);

  return (
    <aside className={`app-sidebar ${buildSidebarClassName(className, isCollapsed)}`}>
      <div className={`sidebar-header border-b border-slate-200 py-4 ${isCollapsed ? 'px-2' : 'px-4 lg:px-5 lg:py-6'}`}>
        <div className={`sidebar-brand flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="sidebar-logo-button flex flex-shrink-0 items-center justify-center rounded-md transition hover:bg-slate-100"
            aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <img
              src={logo}
              alt="Logo Kretos Lab"
              className={`sidebar-logo object-contain ${isCollapsed
                ? 'h-10 w-10'
                : 'h-12 w-12 md:h-12 md:w-12 lg:h-14 lg:w-14'
                }`}
            />
          </button>
          <div className={`min-w-0 flex-1 ${isCollapsed ? 'hidden' : ''} ${brandTextClassName}`}>
            <p className="text-sm font-semibold text-slate-900">Kretos Lab</p>
            <p className="truncate text-xs text-slate-500">
              Sistema de Controle e Análise de Concreto
            </p>
          </div>
          {onToggleCollapse && !isCollapsed ? (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="sidebar-toggle hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 md:inline-flex"
              aria-label="Recolher menu"
            >
              <Pin className="h-4 w-4" />
            </button>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="sidebar-close inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Fechar menu"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
      <nav className="sidebar-nav min-h-0 flex-1 space-y-6 overflow-y-auto overflow-x-hidden px-3 py-5 md:px-2 lg:px-4">
        {visibleSections.map((section, index) => (
          <div key={section.section || `section-${index}`} className="sidebar-group">
            {section.section ? (
              <p
                className={`sidebar-group-title px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 ${sectionClassName}`}
              >
                {section.section}
              </p>
            ) : null}
            <div className="sidebar-links space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `sidebar-link ${linkClassName} ${isActive
                        ? 'bg-[#0e7490]/10 text-[#0e7490]'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span className={linkLabelClassName}>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="sidebar-footer border-t border-slate-200 px-4 py-4 text-xs text-slate-500">
        <p className={`font-medium text-slate-700 ${footerClassName}`}>Ambiente de testes</p>
        <p className={footerClassName}>Dados simulados ativos</p>
      </div>
    </aside>
  );
}

export default Sidebar;
